import { promises as fs } from 'fs';
import { join } from 'path';
import { homedir } from 'os';
import AdmZip from 'adm-zip';
import type { Jai1Config } from '../types/framework.types.js';
import { NetworkError } from '../errors/index.js';

export interface StarterKit {
    id: number;
    slug: string;
    name: string;
    description: string;
    category: string;
    tags: string[];
    version: string;
    downloads: number;
}

export interface StarterKitFull extends StarterKit {
    config: KitConfig;
}

export interface KitConfig {
    slug: string;
    name: string;
    description: string;
    category: string;
    tags?: string[];
    version: string;
    requiredTools?: string[];
    variables?: Variable[];
    hooks?: {
        preInit?: string[];
        postExtract?: string[];
        postInit?: string[];
    };
    framework?: {
        apply: boolean;
        components: string[];
    };
    ide?: {
        promptSync?: boolean;
        defaultTargets?: string[];
    };
    git?: {
        init: boolean;
        initialCommit?: string;
    };
}

export interface Variable {
    name: string;
    prompt: string;
    type?: 'text' | 'select';
    default?: string;
    options?: string[];
    validate?: string;
}

/**
 * Service for managing starter kits
 */
export class StarterKitService {
    /**
     * List available starter kits
     */
    async list(
        config: Jai1Config,
        options?: { category?: string; search?: string }
    ): Promise<StarterKit[]> {
        const params = new URLSearchParams();
        if (options?.category) params.set('category', options.category);
        if (options?.search) params.set('search', options.search);

        const url = `${config.apiUrl}/api/starter-kits${params.toString() ? '?' + params : ''}`;

        const response = await fetch(url, {
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            throw new NetworkError(`Failed to list starter kits: HTTP ${response.status}`);
        }

        const data = (await response.json()) as { kits: StarterKit[] };
        return data.kits;
    }

    /**
     * Get single starter kit with full config
     */
    async get(config: Jai1Config, slug: string): Promise<StarterKitFull> {
        const response = await fetch(`${config.apiUrl}/api/starter-kits/${slug}`, {
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(`Starter kit not found: ${slug}`);
            }
            throw new NetworkError(`Failed to get starter kit: HTTP ${response.status}`);
        }

        return (await response.json()) as StarterKitFull;
    }

    /**
     * Download and extract starter kit
     */
    async downloadAndExtract(
        config: Jai1Config,
        slug: string,
        targetDir: string,
        onProgress?: (percent: number) => void
    ): Promise<void> {
        if (onProgress) onProgress(10);

        // Download zip directly from API (streams from R2)
        const response = await fetch(`${config.apiUrl}/api/starter-kits/${slug}/download`, {
            headers: { 'JAI1-Access-Key': config.accessKey },
        });

        if (!response.ok) {
            throw new NetworkError(`Failed to download kit: HTTP ${response.status}`);
        }

        if (onProgress) onProgress(30);

        // Save to temp file (use ~/.jai1/tmp to avoid macOS sandbox TMPDIR permission issues)
        const tmpBase = join(homedir(), '.jai1', 'tmp');
        await fs.mkdir(tmpBase, { recursive: true });
        const tmpDir = await fs.mkdtemp(join(tmpBase, 'kit-'));
        const tmpFile = join(tmpDir, `${slug}.zip`);

        const buffer = await response.arrayBuffer();
        await fs.writeFile(tmpFile, Buffer.from(buffer));

        if (onProgress) onProgress(60);

        // Extract
        const zip = new AdmZip(tmpFile);
        await fs.mkdir(targetDir, { recursive: true });
        zip.extractAllTo(targetDir, true);

        if (onProgress) onProgress(100);

        // Cleanup
        await fs.rm(tmpDir, { recursive: true, force: true });
    }
}
